import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Search, 
  Filter,
  Hash,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  Edit,
  Copy,
  Trash2,
  CheckCircle,
  XCircle,
  CreditCard,
  GraduationCap,
  Users,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'APPROVED', label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid', color: 'bg-violet-100 text-violet-700' },
  { value: 'PAID', label: 'Fully Paid', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'REJECTED', label: 'Rejected', color: 'bg-red-100 text-red-700' }
];

const ExpensesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  const { selectedFY } = useFinancialYear();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('training');

  // Sort state for CRM ID column (Corporate Training)
  const [crmSort, setCrmSort] = useState('none'); // 'none' | 'asc' | 'desc'

  // Unique CRM IDs dialog
  const [uniqueCrmOpen, setUniqueCrmOpen] = useState(false);
  const [uniqueCrm, setUniqueCrm] = useState(null);
  const [uniqueCrmLoading, setUniqueCrmLoading] = useState(false);

  // Active CRM ID filter (server-side via corporate_project_id)
  const [activeCrmFilter, setActiveCrmFilter] = useState(null);
  const [activeCrmFilterTotal, setActiveCrmFilterTotal] = useState(0);
  
  // Filters - now with multi-select support
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const statusParam = searchParams.get('status');
    return statusParam ? statusParam.split(',') : [];
  });
  const [selectedModes, setSelectedModes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showRejected, setShowRejected] = useState(() => {
    const statusParam = searchParams.get('status');
    return statusParam?.includes('REJECTED') || false;
  });
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  // Stats from dashboard API for KPI cards
  const [expenseStats, setExpenseStats] = useState(null);

  // Mark as Paid modal
  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [expenseToMarkPaid, setExpenseToMarkPaid] = useState(null);
  const [expenseAmount, setExpenseAmount] = useState(0);
  const [paidSoFar, setPaidSoFar] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [paymentModes, setPaymentModes] = useState([]);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [fundBalances, setFundBalances] = useState({});
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);

  const canManage = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';

  // Fetch fund account balances
  const fetchFundBalances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fund-accounts`, {
        headers: getAuthHeaders()
      });
      const balances = {};
      response.data.forEach(account => {
        balances[account.account_type] = account.current_balance;
      });
      setFundBalances(balances);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch fund balances:', error);
    }
  }, [getAuthHeaders]);

  // Fetch payment modes from master lists
  const fetchPaymentModes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/master-lists`, {
        headers: getAuthHeaders()
      });
      const paymentModesList = response.data.find(list => list.list_type === 'payment_modes');
      if (paymentModesList) {
        // Extract just the values (strings) from items
        setPaymentModes(paymentModesList.items.filter(item => item.is_active).map(item => item.value));
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch payment modes:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPaymentModes();
    fetchFundBalances();
  }, [fetchPaymentModes, fetchFundBalances]);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 15);
      
      // Set expense type based on active tab
      const expenseType = activeTab === 'training' ? 'CORPORATE_TRAINING' : 'HR_EXPENSE';
      params.append('expense_type', expenseType);
      
      // FY filter - use financial_year field
      if (selectedFY) {
        params.append('financial_year', selectedFY);
      }

      if (search) params.append('search', search);
      
      // Multi-select payment statuses
      if (selectedStatuses.length > 0) {
        params.append('payment_statuses', selectedStatuses.join(','));
      } else if (!showRejected) {
        // When no explicit filter and rejected toggle is off, exclude rejected
        params.append('payment_statuses', 'PENDING,APPROVED,PARTIALLY_PAID,PAID');
      }
      
      // Multi-select payment modes
      if (selectedModes.length > 0) {
        params.append('modes_of_payment', selectedModes.join(','));
      }

      // CRM ID sort (only for training tab)
      if (activeTab === 'training' && crmSort !== 'none') {
        params.append('sort_by', 'crm_id');
        params.append('sort_order', crmSort);
      }

      // Active CRM ID filter
      if (activeTab === 'training' && activeCrmFilter) {
        params.append('corporate_project_id', activeCrmFilter);
      }

      const response = await axios.get(`${API}/expenses?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      setExpenses(response.data.expenses);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedStatuses, selectedModes, activeTab, selectedFY, showRejected, crmSort, activeCrmFilter, getAuthHeaders]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const openUniqueCrmDialog = async () => {
    setUniqueCrmOpen(true);
    setUniqueCrmLoading(true);
    try {
      const params = selectedFY ? `?financial_year=${selectedFY}` : '';
      const res = await axios.get(`${API}/expenses/unique-crm-ids${params}`, { headers: getAuthHeaders() });
      setUniqueCrm(res.data);
    } catch (e) {
      toast.error('Failed to load unique CRM IDs');
    } finally {
      setUniqueCrmLoading(false);
    }
  };

  const applyCrmIdFilter = (crmId, totalAmount = 0) => {
    if (crmId === '(no ID)') {
      setActiveCrmFilter(null);
      setActiveCrmFilterTotal(0);
    } else {
      setActiveCrmFilter(crmId);
      setActiveCrmFilterTotal(totalAmount);
    }
    setActiveTab('training');
    setPage(1);
    setUniqueCrmOpen(false);
  };

  // Fetch expense KPI stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const params = selectedFY ? `?financial_year=${selectedFY}` : '';
        const res = await axios.get(`${API}/dashboard/stats${params}`, { headers: getAuthHeaders() });
        setExpenseStats(res.data);
      } catch (e) {}
    };
    fetchStats();
  }, [selectedFY, getAuthHeaders]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchExpenses();
  };

  const handleStatusChange = async (expenseId, newStatus, expense = null) => {
    // If marking as paid, open the modal instead
    if (newStatus === 'PAID') {
      setExpenseToMarkPaid(expenseId);
      const amount = expense?.payment_info?.amount_to_pay_inr || expense?.total_expenses || 0;
      const payments = expense?.payments || [];
      const alreadyPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const remaining = Math.max(0, amount - alreadyPaid);
      
      setExpenseAmount(amount);
      setPaidSoFar(alreadyPaid);
      setRemainingAmount(remaining);
      setPaymentAmount(remaining.toString());
      setSelectedPaymentMode('');
      setSelectedAccountType('');
      fetchFundBalances(); // Refresh balances
      setPaidModalOpen(true);
      return;
    }
    
    try {
      await axios.patch(`${API}/expenses/${expenseId}/status`, {
        payment_status: newStatus
      }, {
        headers: getAuthHeaders()
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleMarkAsPaid = async () => {
    if (!expenseToMarkPaid || !selectedPaymentMode) {
      toast.error('Please select a payment mode');
      return;
    }
    
    const amount = parseFloat(paymentAmount) || 0;
    if (amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    if (amount > remainingAmount) {
      toast.error(`Payment amount cannot exceed remaining amount (${formatCurrency(remainingAmount)})`);
      return;
    }
    
    try {
      setMarkingPaid(true);
      // Use the new payments API to properly record the payment and update fund balances
      await axios.post(`${API}/expenses/${expenseToMarkPaid}/payments`, {
        payments: [{
          amount: amount,
          mode_of_payment: selectedPaymentMode,
          payment_date: new Date().toISOString().split('T')[0],
          account_type: selectedAccountType || ''
        }]
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Payment recorded successfully');
      setPaidModalOpen(false);
      setExpenseToMarkPaid(null);
      setSelectedPaymentMode('');
      setSelectedAccountType('');
      setPaymentAmount('');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDelete = async () => {
    if (!expenseToDelete) return;
    try {
      await axios.delete(`${API}/expenses/${expenseToDelete}`, {
        headers: getAuthHeaders()
      });
      toast.success('Expense deleted');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      setDeletingPayment(true);
      await axios.delete(`${API}/expenses/${paymentToDelete.expenseId}/payments/${paymentToDelete.paymentId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Payment deleted and fund transaction reversed');
      setDeletePaymentDialog(false);
      setPaymentToDelete(null);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Parse and format dates - handles various input formats
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    
    // Handle ISO format (2026-02-19 or 2026-02-19T...)
    if (dateStr.includes('-') && dateStr.length >= 10) {
      const parts = dateStr.substring(0, 10).split('-');
      if (parts[0].length === 4 && !isNaN(Date.parse(dateStr.substring(0, 10)))) {
        const date = new Date(parts[0], parts[1] - 1, parts[2]);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = date.getDate().toString().padStart(2, '0');
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
    }
    
    // Handle dd-Mon-yyyy format (19-Feb-2026) - return as is
    const match = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    if (match) {
      return dateStr;
    }
    
    return dateStr;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'badge-pending', label: 'Pending' },
      APPROVED: { class: 'badge-approved', label: 'Approved' },
      PARTIALLY_PAID: { class: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Partial' },
      PAID: { class: 'badge-paid', label: 'Paid' },
      REJECTED: { class: 'badge-rejected', label: 'Rejected' },
      ON_HOLD: { class: 'badge-on-hold', label: 'On Hold' }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <Badge className={`${config.class} font-mono text-xs`}>{config.label}</Badge>;
  };

  const renderExpenseActions = (expense, index) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`expense-menu-${index}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate(`/expenses/${expense.id}`)}>
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/expenses/${expense.id}/edit`)}>
          <Edit className="w-4 h-4 mr-2" />
          Edit
        </DropdownMenuItem>
        {expense.expense_type === 'HR_EXPENSE' && (
          <DropdownMenuItem
            onClick={() => navigate(`/expenses/new?clone=${expense.id}`)}
            data-testid={`expense-clone-${index}`}
          >
            <Copy className="w-4 h-4 mr-2 text-indigo-600" />
            Clone (new entry)
          </DropdownMenuItem>
        )}
        {canManage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => handleStatusChange(expense.id, 'APPROVED')}
              disabled={expense.payment_info?.payment_status === 'APPROVED'}
            >
              <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
              Approve
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleStatusChange(expense.id, 'PAID', expense)}
              disabled={expense.payment_info?.payment_status === 'PAID'}
            >
              <CheckCircle className="w-4 h-4 mr-2 text-blue-500" />
              Mark as Paid
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleStatusChange(expense.id, 'REJECTED')}
              disabled={expense.payment_info?.payment_status === 'REJECTED'}
            >
              <XCircle className="w-4 h-4 mr-2 text-red-500" />
              Reject
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-red-600"
              onClick={() => { setExpenseToDelete(expense.id); setDeleteDialogOpen(true); }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderTrainingTable = () => (
    <table className="w-full data-table">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Client Name</th>
          <th
            className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:bg-slate-100"
            onClick={() => setCrmSort(s => s === 'none' ? 'asc' : s === 'asc' ? 'desc' : 'none')}
            data-testid="expense-crm-sort-header"
            title="Click to toggle sort: A→Z → Z→A → none"
          >
            Zoho CRM ID
            <span className="ml-2 text-xs text-indigo-600">
              {crmSort === 'asc' ? '▲' : crmSort === 'desc' ? '▼' : '↕'}
            </span>
          </th>
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Training Name</th>
          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Amount</th>
          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Source</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={7} className="py-12 text-center">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-950"></div>
              </div>
            </td>
          </tr>
        ) : expenses.length > 0 ? (
          expenses.map((expense, index) => (
            <tr 
              key={expense.id} 
              className="border-b border-slate-100 hover:bg-indigo-50 transition-colors cursor-pointer"
              data-testid={`expense-row-${index}`}
              onClick={() => navigate(`/expenses/${expense.id}`)}
            >
              <td className="py-3 px-4 font-mono text-sm text-slate-600">
                {formatDateDisplay(expense.reporting_date || expense.created_at?.split('T')[0])}
              </td>
              <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                {expense.company_name || '-'}
              </td>
              <td className="py-3 px-4 font-mono text-sm text-slate-700">
                {expense.corporate_project_id_new || expense.corporate_project_id || '-'}
              </td>
              <td className="py-3 px-4 text-sm text-slate-900 max-w-[200px] truncate">
                {expense.training_name || '-'}
              </td>
              <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-indigo-950">
                {formatCurrency(expense.payment_info?.amount_to_pay_inr || expense.total_expenses)}
              </td>
              <td className="py-3 px-4 text-center">
                {getStatusBadge(expense.payment_info?.payment_status)}
              </td>
              <td className="py-3 px-4 text-center">
                <Badge variant="outline" className={`text-xs ${expense.source === 'ZOHO_WEBHOOK' ? 'border-emerald-300 text-emerald-700' : 'border-slate-300 text-slate-600'}`}>
                  {expense.source === 'ZOHO_WEBHOOK' ? 'Zoho' : 'Manual'}
                </Badge>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={7} className="py-16 text-center">
              <div className="empty-state">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <GraduationCap className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No training expenses found</h3>
                <p className="text-slate-500 text-sm mb-4">Try adjusting your filters or add a new expense</p>
                <Button onClick={() => navigate('/expenses/new')} className="bg-indigo-950 hover:bg-indigo-900">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Training Expense
                </Button>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderHRTable = () => (
    <table className="w-full data-table">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Description</th>
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Category</th>
          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Payee Name</th>
          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Amount</th>
          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Payment Mode</th>
          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={8} className="py-12 text-center">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-950"></div>
              </div>
            </td>
          </tr>
        ) : expenses.length > 0 ? (
          expenses.map((expense, index) => (
            <tr 
              key={expense.id} 
              className="border-b border-slate-100 hover:bg-indigo-50 transition-colors cursor-pointer"
              data-testid={`expense-row-${index}`}
              onClick={() => navigate(`/expenses/${expense.id}`)}
            >
              <td className="py-3 px-4 font-mono text-sm text-slate-600">
                {formatDateDisplay(expense.reporting_date || expense.created_at?.split('T')[0])}
              </td>
              <td className="py-3 px-4 text-sm text-slate-900 font-medium max-w-[200px] truncate">
                {expense.description || '-'}
              </td>
              <td className="py-3 px-4 text-sm text-slate-700">
                {expense.category ? (
                  <Badge variant="outline" className="border-indigo-200 text-indigo-700 bg-indigo-50">
                    {expense.category}
                  </Badge>
                ) : '-'}
              </td>
              <td className="py-3 px-4 text-sm text-slate-900">
                {expense.vendor_name || '-'}
              </td>
              <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-indigo-950">
                {formatCurrency(expense.payment_info?.amount_to_pay_inr || expense.total_expenses)}
              </td>
              <td className="py-3 px-4 text-center">
                {getStatusBadge(expense.payment_info?.payment_status)}
              </td>
              <td className="py-3 px-4 text-center text-sm text-slate-600">
                {expense.payment_info?.mode_of_payment || '-'}
              </td>
              <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                {renderExpenseActions(expense, index)}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={8} className="py-16 text-center">
              <div className="empty-state">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No HR expenses found</h3>
                <p className="text-slate-500 text-sm mb-4">Try adjusting your filters or add a new expense</p>
                <Button onClick={() => navigate('/expenses/new')} className="bg-indigo-950 hover:bg-indigo-900">
                  <Plus className="w-4 h-4 mr-2" />
                  Add HR Expense
                </Button>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-indigo-950">Expenses</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage corporate training and HR expenses
            <span className="ml-2 text-xs text-slate-400">Showing FY {selectedFY}</span>
          </p>
        </div>
        <Button 
          className="bg-indigo-950 hover:bg-indigo-900"
          onClick={() => navigate('/expenses/new')}
          data-testid="add-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </div>

      {/* KPI Cards */}
      {expenseStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-amber-200 bg-amber-50" onClick={() => { setSelectedStatuses(['PENDING']); setShowRejected(false); setPage(1); }} data-testid="kpi-pending">
            <CardContent className="p-3"><p className="text-xs text-amber-600">Pending</p><p className="text-lg font-bold text-amber-800">{expenseStats.pending_count}</p><p className="text-xs text-amber-500 font-mono">{formatCurrency(expenseStats.pending_amount)}</p></CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-blue-200 bg-blue-50" onClick={() => { setSelectedStatuses(['APPROVED']); setShowRejected(false); setPage(1); }} data-testid="kpi-approved">
            <CardContent className="p-3"><p className="text-xs text-blue-600">Approved</p><p className="text-lg font-bold text-blue-800">{expenseStats.approved_count}</p><p className="text-xs text-blue-500 font-mono">{formatCurrency(expenseStats.approved_amount)}</p></CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-violet-200 bg-violet-50" onClick={() => { setSelectedStatuses(['PARTIALLY_PAID']); setShowRejected(false); setPage(1); }} data-testid="kpi-partial">
            <CardContent className="p-3"><p className="text-xs text-violet-600">Partially Paid</p><p className="text-lg font-bold text-violet-800">{expenseStats.partially_paid_count}</p><p className="text-xs text-violet-500 font-mono">{formatCurrency(expenseStats.partially_paid_amount)}</p></CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-emerald-200 bg-emerald-50" onClick={() => { setSelectedStatuses(['PAID']); setShowRejected(false); setPage(1); }} data-testid="kpi-paid">
            <CardContent className="p-3"><p className="text-xs text-emerald-600">Paid</p><p className="text-lg font-bold text-emerald-800">{expenseStats.paid_count}</p><p className="text-xs text-emerald-500 font-mono">{formatCurrency(expenseStats.paid_amount)}</p></CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow border-red-200 bg-red-50" onClick={() => { setSelectedStatuses(['REJECTED']); setShowRejected(true); setPage(1); }} data-testid="kpi-rejected">
            <CardContent className="p-3"><p className="text-xs text-red-600">Rejected</p><p className="text-lg font-bold text-red-800">{expenseStats.rejected_count}</p><p className="text-xs text-red-500 font-mono">{formatCurrency(expenseStats.rejected_amount)}</p></CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="training" className="data-[state=active]:bg-white" data-testid="training-tab">
            <GraduationCap className="w-4 h-4 mr-2" />
            Corporate Training
          </TabsTrigger>
          <TabsTrigger value="hr" className="data-[state=active]:bg-white" data-testid="hr-tab">
            <Users className="w-4 h-4 mr-2" />
            HR Expenses
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="space-y-4">
              {/* Search and Toggle */}
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder={activeTab === 'training' 
                        ? "Search by client, training name, CRM ID..." 
                        : "Search by description, payee..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchExpenses()}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>

                <Button 
                  type="button" 
                  variant={showFilters ? "default" : "outline"}
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? "bg-indigo-600" : ""}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {(selectedStatuses.length > 0 || selectedModes.length > 0) && (
                    <Badge className="ml-2 bg-indigo-100 text-indigo-700">
                      {selectedStatuses.length + selectedModes.length}
                    </Badge>
                  )}
                </Button>

                {activeTab === 'training' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openUniqueCrmDialog}
                    data-testid="unique-crm-btn"
                    title="View unique Zoho CRM IDs in current FY"
                  >
                    <Hash className="w-4 h-4 mr-2" />
                    Unique CRM IDs
                  </Button>
                )}

                {activeCrmFilter && (
                  <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-700 font-mono" data-testid="active-crm-filter">
                    CRM ID: {activeCrmFilter}
                    <button
                      type="button"
                      className="ml-2 hover:text-indigo-900"
                      onClick={() => { setActiveCrmFilter(null); setActiveCrmFilterTotal(0); setPage(1); }}
                      aria-label="Clear CRM filter"
                    >
                      <X className="w-3 h-3 inline" />
                    </button>
                  </Badge>
                )}

                {(selectedStatuses.length > 0 || selectedModes.length > 0) && (
                  <Button 
                    type="button" 
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedStatuses([]); setSelectedModes([]); setShowRejected(false); setPage(1); }}
                    className="text-slate-500"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear filters
                  </Button>
                )}

                <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto">
                  <input type="checkbox" checked={showRejected} onChange={(e) => { setShowRejected(e.target.checked); if (!e.target.checked && selectedStatuses.includes('REJECTED')) { setSelectedStatuses(selectedStatuses.filter(s => s !== 'REJECTED')); } setPage(1); }} className="rounded border-slate-300" data-testid="show-rejected-toggle" />
                  <span className="text-slate-600">Show Rejected</span>
                </label>
              </div>

              {/* Expanded Filters */}
              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-200">
                  {/* Payment Status Multi-select */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Payment Status</Label>
                    <div className="space-y-2">
                      {PAYMENT_STATUSES.map(status => (
                        <div key={status.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`status-${status.value}`}
                            checked={selectedStatuses.includes(status.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedStatuses([...selectedStatuses, status.value]);
                              } else {
                                setSelectedStatuses(selectedStatuses.filter(s => s !== status.value));
                              }
                              setPage(1);
                            }}
                          />
                          <label
                            htmlFor={`status-${status.value}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Payment Mode Multi-select */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Payment Mode</Label>
                    <div className="space-y-2">
                      {paymentModes.map(mode => (
                        <div key={mode} className="flex items-center space-x-2">
                          <Checkbox
                            id={`mode-${mode}`}
                            checked={selectedModes.includes(mode)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedModes([...selectedModes, mode]);
                              } else {
                                setSelectedModes(selectedModes.filter(m => m !== mode));
                              }
                              setPage(1);
                            }}
                          />
                          <label
                            htmlFor={`mode-${mode}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {mode}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Active Filters Display */}
              {(selectedStatuses.length > 0 || selectedModes.length > 0) && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {selectedStatuses.map(status => {
                    const statusConfig = PAYMENT_STATUSES.find(s => s.value === status);
                    return (
                      <Badge 
                        key={status} 
                        className={`${statusConfig?.color || 'bg-slate-100'} cursor-pointer`}
                        onClick={() => setSelectedStatuses(selectedStatuses.filter(s => s !== status))}
                      >
                        {statusConfig?.label || status}
                        <X className="w-3 h-3 ml-1" />
                      </Badge>
                    );
                  })}
                  {selectedModes.map(mode => (
                    <Badge 
                      key={mode} 
                      variant="outline"
                      className="cursor-pointer"
                      onClick={() => setSelectedModes(selectedModes.filter(m => m !== mode))}
                    >
                      {mode}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* CRM ID Filter Summary Card */}
        {activeCrmFilter && (
          <Card className="border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-white" data-testid="crm-filter-summary">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide">Filtered by CRM ID</p>
                  <p className="text-2xl font-bold text-indigo-950 font-mono">{activeCrmFilter}</p>
                  <p className="text-xs text-slate-500 mt-1">{total} expense{total === 1 ? '' : 's'} in FY {selectedFY}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Total Amount</p>
                  <p className="text-3xl font-bold text-emerald-700 font-mono" data-testid="crm-filter-total">{formatCurrency(activeCrmFilterTotal)}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setActiveCrmFilter(null); setActiveCrmFilterTotal(0); setPage(1); }}
                  data-testid="clear-crm-filter-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Clear filter
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Training Tab Content */}
        <TabsContent value="training" className="mt-0">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                {renderTrainingTable()}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HR Tab Content */}
        <TabsContent value="hr" className="mt-0">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                {renderHRTable()}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Paid Modal */}
      <Dialog open={paidModalOpen} onOpenChange={setPaidModalOpen}>
        <DialogContent className="sm:max-w-[425px]" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Mark as Paid
            </DialogTitle>
            <DialogDescription>
              Enter payment amount and select payment mode.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Payment Summary */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Total Amount</span>
                <span className="font-mono font-bold text-indigo-950">
                  {formatCurrency(expenseAmount)}
                </span>
              </div>
              {paidSoFar > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Already Paid</span>
                  <span className="font-mono font-semibold text-emerald-600">
                    {formatCurrency(paidSoFar)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-2">
                <span className="text-sm font-medium text-slate-700">Remaining to Pay</span>
                <span className="font-mono font-bold text-indigo-950">
                  {formatCurrency(remainingAmount)}
                </span>
              </div>
            </div>

            {/* Payment Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="payment-amount" className="text-sm font-medium">
                Payment Amount <span className="text-red-500">*</span>
              </Label>
              <Input
                id="payment-amount"
                type="number"
                min="0"
                max={remainingAmount}
                step="0.01"
                value={paymentAmount}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  if (val > remainingAmount) {
                    setPaymentAmount(remainingAmount.toString());
                  } else {
                    setPaymentAmount(e.target.value);
                  }
                }}
                placeholder="Enter amount"
                className="font-mono"
                data-testid="payment-amount-input"
              />
              {parseFloat(paymentAmount) > remainingAmount && (
                <p className="text-xs text-red-500">Cannot exceed remaining amount</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-mode" className="text-sm font-medium">
                Mode of Payment <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={selectedPaymentMode} 
                onValueChange={setSelectedPaymentMode}
              >
                <SelectTrigger className="w-full" data-testid="payment-mode-select">
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  {paymentModes.map((mode) => (
                    <SelectItem key={mode.value} value={mode.label}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Account Type Override */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Fund Account <span className="text-xs text-slate-400 font-normal">(where money came from)</span>
              </Label>
              <Select
                value={selectedAccountType}
                onValueChange={setSelectedAccountType}
              >
                <SelectTrigger className="w-full" data-testid="account-type-select">
                  <SelectValue placeholder="Auto (based on mode)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ONLINE">Online</SelectItem>
                  <SelectItem value="DEPOSIT">Deposit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Show fund balance based on selected account type or mode */}
            {(selectedAccountType || (selectedPaymentMode && selectedPaymentMode !== 'Online')) && (
              <div className={`p-3 rounded-lg border ${
                (() => {
                  const accountType = selectedAccountType || (selectedPaymentMode === 'UPI' ? 'OMNICARD' : selectedPaymentMode.toUpperCase());
                  const balance = fundBalances[accountType] || 0;
                  const amount = parseFloat(paymentAmount) || 0;
                  return balance < amount 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-emerald-50 border-emerald-200';
                })()
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">
                      {selectedAccountType || (selectedPaymentMode === 'UPI' ? 'Omnicard' : selectedPaymentMode)} Balance
                    </p>
                    <p className="text-lg font-bold font-mono">
                      {formatCurrency(
                        fundBalances[selectedAccountType || (selectedPaymentMode === 'UPI' ? 'OMNICARD' : selectedPaymentMode.toUpperCase())] || 0
                      )}
                    </p>
                  </div>
                  {(() => {
                    const accountType = selectedAccountType || (selectedPaymentMode === 'UPI' ? 'OMNICARD' : selectedPaymentMode.toUpperCase());
                    const balance = fundBalances[accountType] || 0;
                    const amount = parseFloat(paymentAmount) || 0;
                    if (balance < amount) {
                      return (
                        <div className="text-amber-600 text-xs flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Low Balance
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                {(() => {
                  const accountType = selectedAccountType || (selectedPaymentMode === 'UPI' ? 'OMNICARD' : selectedPaymentMode.toUpperCase());
                  const balance = fundBalances[accountType] || 0;
                  const amount = parseFloat(paymentAmount) || 0;
                  const newBalance = balance - amount;
                  return (
                    <p className="text-xs text-slate-500 mt-1">
                      After payment: {formatCurrency(newBalance)}
                    </p>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPaidModalOpen(false)}
              data-testid="cancel-paid-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleMarkAsPaid}
              disabled={!selectedPaymentMode || markingPaid || !paymentAmount || parseFloat(paymentAmount) <= 0 || parseFloat(paymentAmount) > remainingAmount}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-paid-btn"
            >
              {markingPaid ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Confirmation */}
      <AlertDialog open={deletePaymentDialog} onOpenChange={setDeletePaymentDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this payment of {formatCurrency(paymentToDelete?.amount)} and reverse the fund transaction.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePayment} disabled={deletingPayment} className="bg-red-600 hover:bg-red-700">
              {deletingPayment ? 'Deleting...' : 'Delete Payment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unique CRM IDs Dialog */}
      <Dialog open={uniqueCrmOpen} onOpenChange={setUniqueCrmOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="unique-crm-dialog">
          <DialogHeader>
            <DialogTitle>Unique Zoho CRM IDs · FY {selectedFY}</DialogTitle>
            <DialogDescription>
              Corporate Training expenses grouped by CRM ID. Click any ID to filter the list.
            </DialogDescription>
          </DialogHeader>
          {uniqueCrmLoading && <p className="text-slate-500 text-sm">Loading...</p>}
          {!uniqueCrmLoading && uniqueCrm && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                  <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide">Unique IDs</p>
                  <p className="text-2xl font-bold text-indigo-700 font-mono">{uniqueCrm.unique_count}</p>
                </div>
                <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs text-slate-600 font-semibold uppercase tracking-wide">Total Expenses</p>
                  <p className="text-2xl font-bold text-slate-700 font-mono">{uniqueCrm.total_expense_count}</p>
                </div>
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Total Amount</p>
                  <p className="text-xl font-bold text-emerald-700 font-mono">{formatCurrency(uniqueCrm.total_amount)}</p>
                </div>
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm" data-testid="unique-crm-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">CRM ID</th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Client(s)</th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-600">Count</th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniqueCrm.rows.length === 0 ? (
                      <tr><td colSpan={4} className="py-8 text-center text-slate-500">No CRM IDs found in this FY</td></tr>
                    ) : uniqueCrm.rows.map((r, i) => (
                      <tr
                        key={r.crm_id + i}
                        className="border-t hover:bg-indigo-50 cursor-pointer"
                        onClick={() => applyCrmIdFilter(r.crm_id, r.total_amount)}
                        data-testid={`unique-crm-row-${i}`}
                      >
                        <td className="py-2 px-3 font-mono font-semibold text-indigo-700">{r.crm_id}</td>
                        <td className="py-2 px-3 text-slate-700 max-w-[260px] truncate">
                          {(r.client_names || []).filter(Boolean).join(', ') || '-'}
                        </td>
                        <td className="py-2 px-3 text-center font-mono">{r.count}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold text-emerald-700">{formatCurrency(r.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500">Click a CRM ID to filter expenses to that ID only.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
